import { useLayoutEffect, useMemo, useRef, useState, type MouseEvent } from 'react'
import { max as d3Max } from 'd3-array'
import type { SpendAnalyticsBasis, SpendAnalyticsPeriod } from '@/services/vendorDetails'
import { formatAxisUsdCompact, formatUsd, niceAxisScale } from '@/components/gcp/gcpHelpers'

const CHART_COLORS = [
  '#2563eb',
  '#7c3aed',
  '#059669',
  '#d97706',
  '#dc2626',
  '#6b7280',
]

const TOOLTIP_OFFSET = 12
const TOOLTIP_ESTIMATE_WIDTH = 190

interface PeriodStack {
  period: string
  periodTotal: number
  segments: Array<{ segment: string; value: number; color: string }>
}

interface HoverState {
  clientX: number
  clientY: number
  period: string
  segment: string
  value: number
  periodTotal: number
}

function formatPeriodLabel(periodKey: string, granularity: 'month' | 'quarter' | 'year'): string {
  if (granularity === 'year') return periodKey
  if (granularity === 'quarter') {
    const match = periodKey.match(/^(\d{4})-Q(\d)$/)
    if (match) return `Q${match[2]} ${match[1]}`
    return periodKey
  }
  const [year, month] = periodKey.split('-')
  if (!year || !month) return periodKey
  const date = new Date(Number(year), Number(month) - 1, 1)
  if (Number.isNaN(date.getTime())) return periodKey
  return date.toLocaleString('en-US', { month: 'short', year: '2-digit' })
}

interface VendorSpendTrendChartProps {
  periods: SpendAnalyticsPeriod[]
  basis: SpendAnalyticsBasis
  granularity: 'month' | 'quarter' | 'year'
  segmentLabel?: string
}

export default function VendorSpendTrendChart({
  periods,
  basis,
  granularity,
  segmentLabel = 'category',
}: VendorSpendTrendChartProps) {
  const chartRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [hover, setHover] = useState<HoverState | null>(null)
  const [tooltipPos, setTooltipPos] = useState<{ left: number; top: number } | null>(null)
  const [activeSegment, setActiveSegment] = useState<string | null>(null)

  const { stacks, legendSegments } = useMemo(() => {
    if (periods.length === 0) {
      return { stacks: [] as PeriodStack[], legendSegments: [] as string[] }
    }

    const segmentTotals = new Map<string, number>()
    for (const period of periods) {
      for (const row of period.by_segment) {
        segmentTotals.set(row.segment, (segmentTotals.get(row.segment) ?? 0) + row.amount)
      }
    }

    const topSegments = [...segmentTotals.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([segment]) => segment)

    const stacks: PeriodStack[] = periods.map((period) => {
      const periodTotal = basis === 'net' ? period.net : period.gross
      const otherValue = period.by_segment
        .filter((row) => !topSegments.includes(row.segment))
        .reduce((sum, row) => sum + row.amount, 0)

      const segments = topSegments.map((segment, index) => ({
        segment,
        value: period.by_segment.find((row) => row.segment === segment)?.amount ?? 0,
        color: CHART_COLORS[index % CHART_COLORS.length]!,
      }))

      if (Math.abs(otherValue) > 1e-9) {
        segments.push({ segment: 'Other', value: otherValue, color: CHART_COLORS[5]! })
      }

      return { period: period.period, periodTotal, segments }
    })

    const legendSegments = [...topSegments]
    if (stacks.some((stack) => stack.segments.some((seg) => seg.segment === 'Other'))) {
      legendSegments.push('Other')
    }

    return { stacks, legendSegments }
  }, [periods, basis])

  useLayoutEffect(() => {
    if (!hover || !chartRef.current) {
      setTooltipPos(null)
      return
    }

    const rect = chartRef.current.getBoundingClientRect()
    const tooltipWidth = tooltipRef.current?.offsetWidth ?? TOOLTIP_ESTIMATE_WIDTH
    const tooltipHeight = tooltipRef.current?.offsetHeight ?? 72

    let left = hover.clientX - rect.left + TOOLTIP_OFFSET
    if (left + tooltipWidth > rect.width - 4) {
      left = hover.clientX - rect.left - tooltipWidth - TOOLTIP_OFFSET
    }
    left = Math.max(4, Math.min(left, rect.width - tooltipWidth - 4))

    let top = hover.clientY - rect.top + TOOLTIP_OFFSET
    top = Math.max(4, Math.min(top, rect.height - tooltipHeight - 4))

    setTooltipPos({ left, top })
  }, [hover])

  if (stacks.length === 0) {
    return <p className="text-sm text-gray-500">No spend in this date range.</p>
  }

  const width = 520
  const height = 260
  const margin = { top: 16, right: 12, bottom: 44, left: 44 }
  const innerWidth = width - margin.left - margin.right
  const innerHeight = height - margin.top - margin.bottom
  const dataMax = d3Max(stacks, (stack) => stack.periodTotal) ?? 0
  const { max: yMax, ticks: yTicks } = niceAxisScale(dataMax, 5)
  const gap = stacks.length > 12 ? 4 : 8
  const barWidth = Math.max(12, innerWidth / stacks.length - gap)

  const updateHover = (
    event: MouseEvent<SVGRectElement>,
    stack: PeriodStack,
    segment: PeriodStack['segments'][number]
  ) => {
    setHover({
      clientX: event.clientX,
      clientY: event.clientY,
      period: stack.period,
      segment: segment.segment,
      value: segment.value,
      periodTotal: stack.periodTotal,
    })
  }

  return (
    <div ref={chartRef} className="relative space-y-3">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-auto w-full max-w-full"
        role="img"
        aria-label={`Vendor spend trend by ${segmentLabel}`}
      >
        <g transform={`translate(${margin.left},${margin.top})`}>
          {yTicks.map((value) => {
            const y = innerHeight - (yMax > 0 ? (value / yMax) * innerHeight : 0)
            return (
              <g key={value}>
                <line x1={0} y1={y} x2={innerWidth} y2={y} stroke="#e5e7eb" />
                <text x={-8} y={y + 4} textAnchor="end" className="fill-gray-500 text-[10px]">
                  {formatAxisUsdCompact(value)}
                </text>
              </g>
            )
          })}

          {stacks.map((stack, index) => {
            const x = index * (barWidth + gap) + gap / 2
            const barTotalHeight =
              yMax > 0 && stack.periodTotal > 0
                ? (stack.periodTotal / yMax) * innerHeight
                : 0
            let yOffset = innerHeight
            return (
              <g key={stack.period} transform={`translate(${x},0)`}>
                {stack.segments.map((segment) => {
                  const segHeight =
                    stack.periodTotal > 0
                      ? (segment.value / stack.periodTotal) * barTotalHeight
                      : 0
                  const rectHeight = Math.abs(segHeight)
                  const rectY = segHeight >= 0 ? yOffset - rectHeight : yOffset
                  yOffset -= segHeight
                  const dimmed =
                    activeSegment != null && activeSegment !== segment.segment
                  return (
                    <rect
                      key={`${stack.period}-${segment.segment}`}
                      x={0}
                      y={rectY}
                      width={barWidth}
                      height={rectHeight}
                      fill={segment.color}
                      opacity={dimmed ? 0.2 : 1}
                      className="cursor-pointer transition-opacity"
                      onMouseEnter={(e) => {
                        setActiveSegment(segment.segment)
                        updateHover(e, stack, segment)
                      }}
                      onMouseMove={(e) => updateHover(e, stack, segment)}
                      onMouseLeave={() => {
                        setActiveSegment(null)
                        setHover(null)
                      }}
                    />
                  )
                })}
                <text
                  x={barWidth / 2}
                  y={innerHeight + 16}
                  textAnchor="middle"
                  className="fill-gray-600 text-[9px]"
                >
                  {formatPeriodLabel(stack.period, granularity)}
                </text>
              </g>
            )
          })}
          <line x1={0} y1={innerHeight} x2={innerWidth} y2={innerHeight} stroke="#d1d5db" />
        </g>
      </svg>

      {hover ? (
        <div
          ref={tooltipRef}
          className="pointer-events-none absolute z-10 min-w-[10rem] rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg"
          style={{
            left: tooltipPos?.left ?? hover.clientX,
            top: tooltipPos?.top ?? hover.clientY,
            visibility: tooltipPos ? 'visible' : 'hidden',
          }}
        >
          <p className="font-medium text-gray-900">
            {formatPeriodLabel(hover.period, granularity)}
          </p>
          <p className="mt-1 text-gray-700">
            {hover.segment}: {formatUsd(hover.value)}
          </p>
          <p className="text-gray-500">Period total: {formatUsd(hover.periodTotal)}</p>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2 text-xs text-gray-600">
        {legendSegments.map((segment, index) => (
          <button
            key={segment}
            type="button"
            className={`inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 transition-opacity ${
              activeSegment != null && activeSegment !== segment ? 'opacity-40' : ''
            }`}
            onMouseEnter={() => setActiveSegment(segment)}
            onMouseLeave={() => setActiveSegment(null)}
          >
            <span
              className="inline-block h-2.5 w-2.5 rounded-sm"
              style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
            />
            <span className="max-w-[8rem] truncate" title={segment}>
              {segment}
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}
